<template>
  <section class="section lottery-section">
    <slot name="header">
      <SectionHeader
        :title="title"
        variant="simple"
      />
    </slot>
    
    <LotteryFilters
      v-if="showFilters"
      :active-country-slugs="activeCountrySlugs"
      @search="handleSearch"
      @filter-country="handleCountryFilter"
    />
    
    <div
      class="lottery-container"
      :class="{ 'has-swiper': cardLayout === 'vertical' }"
    >
      <!-- Swiper navigation for vertical layout -->
      <button 
        v-if="cardLayout === 'vertical'" 
        class="swiper-nav swiper-nav-prev" 
        :class="{ 'is-visible': canScrollPrev }"
        @click="scrollPrev"
      >
        <svg
          width="10"
          height="16"
          viewBox="0 0 10 16"
          fill="none"
        >
          <path
            d="M8 2L2 8L8 14"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      
      <div
        ref="gridRef"
        class="lottery-grid"
        :class="[cardLayout]"
        @scroll="updateNavVisibility"
      >
        <div
          v-for="lottery in filteredLotteries"
          :key="`${lottery.game_slug}-${lottery.id}`"
        >
          <transition
            name="fade"
            @after-leave="() => finalizeRemove(lottery)"
          >
            <LotteryCard
              v-if="!lottery._removing"
              :lottery="lottery"
              :layout="cardLayout"
              :is-favorited="isFavorited"
              @favorite="() => $emit('favorite', lottery)"
              @bet="() => $emit('bet', lottery)"
              @remove="handleRemove(lottery)"
            />
          </transition>
        </div>
      </div>
      
      <!-- Swiper navigation for vertical layout -->
      <button 
        v-if="cardLayout === 'vertical'" 
        class="swiper-nav swiper-nav-next" 
        :class="{ 'is-visible': canScrollNext }"
        @click="scrollNext"
      >
        <svg
          width="10"
          height="16"
          viewBox="0 0 10 16"
          fill="none"
        >
          <path
            d="M2 2L8 8L2 14"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </section>
</template>

<script lang="ts">
export default {
  name: 'LotterySection'
}
</script>

<script setup lang="ts">
import { ref, defineProps, defineEmits, watch, computed, withDefaults, onMounted, onUnmounted, nextTick } from 'vue'
import LotteryCard from '@/components/LotteryCard.vue'
import LotteryFilters from '@/components/LotteryFilters.vue'
import SectionHeader from '@/components/base/SectionHeader.vue'
import type { UpcomingDraw } from '@/types/lottery'
import { useIntegrationMode } from '@/composables/useIntegrationMode'

const { isLite } = useIntegrationMode()

const props = withDefaults(defineProps<{
  title?: string
  lotteries: UpcomingDraw[]
  isFavorited?: (draw: UpcomingDraw) => boolean
  showFilters?: boolean
  layout?: 'vertical' | 'horizontal'
}>(), {
  title: '',
  layout: 'horizontal',
  showFilters: true,
  isFavorited: () => false
})

const cardLayout = computed(() => (isLite.value ? 'horizontal' : props.layout))

const canScrollPrev = ref(false)
const canScrollNext = ref(false)
const isMobile = ref(false)
let resizeObserver: ResizeObserver | null = null

const getScrollStep = () => {
  if (!gridRef.value) return isMobile.value ? 168 : 222.78

  const firstItem = gridRef.value.firstElementChild as HTMLElement | null
  if (!firstItem) return isMobile.value ? 168 : 222.78

  const styles = window.getComputedStyle(gridRef.value)
  const gapValue = parseFloat(styles.columnGap || styles.gap || '0')

  return firstItem.getBoundingClientRect().width + gapValue
}

const updateNavVisibility = () => {
  if (gridRef.value && props.layout === 'vertical') {
    const { scrollWidth, clientWidth, scrollLeft } = gridRef.value
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth)
    const threshold = 2

    canScrollPrev.value = scrollLeft > threshold
    canScrollNext.value = maxScrollLeft - scrollLeft > threshold
  } else {
    canScrollPrev.value = false
    canScrollNext.value = false
  }
}

const scrollPrev = () => {
  if (gridRef.value) {
    gridRef.value.scrollBy({ left: -getScrollStep(), behavior: 'smooth' })
    window.setTimeout(updateNavVisibility, 250)
  }
}

const scrollNext = () => {
  if (gridRef.value) {
    gridRef.value.scrollBy({ left: getScrollStep(), behavior: 'smooth' })
    window.setTimeout(updateNavVisibility, 250)
  }
}

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
  updateNavVisibility()
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)

  nextTick(() => {
    updateNavVisibility()

    if (gridRef.value && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateNavVisibility()
      })
      resizeObserver.observe(gridRef.value)
    }

    window.setTimeout(updateNavVisibility, 100)
    window.setTimeout(updateNavVisibility, 350)
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  resizeObserver?.disconnect()
})

const emit = defineEmits<{
  (e: 'favorite', draw: UpcomingDraw): void
  (e: 'bet', draw: UpcomingDraw): void
  (e: 'removeLottery', draw: UpcomingDraw): void
}>()

// Local copy with _removing property
const localLotteries = ref((props.lotteries || []).map(l => ({ ...l, _removing: false } as UpcomingDraw & { _removing: boolean })));

// Filter states
const searchQuery = ref('');
const selectedCountry = ref('');

/** Countries that currently have draws in this section (for mobile filter UX). */
const activeCountrySlugs = computed(() => {
  const slugs = new Set<string>();
  for (const draw of localLotteries.value) {
    if (draw.country) slugs.add(String(draw.country).toLowerCase());
  }
  return Array.from(slugs);
});

// Filtered lotteries based on search and country filters
const filteredLotteries = computed(() => {
  let filtered = [...localLotteries.value];

  // Apply search filter
  if (searchQuery.value) {
    filtered = filtered.filter(draw => 
      (draw.game_name && draw.game_name.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
      (draw.country && draw.country.toLowerCase().includes(searchQuery.value.toLowerCase()))
    );
  }

  // Apply country filter
  if (selectedCountry.value) {
    filtered = filtered.filter(draw => draw.country === selectedCountry.value);
  }

  return filtered;
});

// Filter handlers
const handleSearch = (query: string) => {
  searchQuery.value = query;
};

const handleCountryFilter = (country: string) => {
  selectedCountry.value = country;
};

watch(() => props.lotteries, (newVal) => {
  localLotteries.value = (newVal || []).map(l => ({ ...l, _removing: false }));
  nextTick(() => {
    updateNavVisibility()
  })
});

watch(filteredLotteries, () => {
  nextTick(() => {
    updateNavVisibility()
  })
})

const handleRemove = (lotteryToRemove: UpcomingDraw) => {
  const idx = localLotteries.value.findIndex(l => l.id === lotteryToRemove.id);
  if (idx !== -1) {
    localLotteries.value[idx]._removing = true;
  }
};

const finalizeRemove = (lotteryToRemove: UpcomingDraw) => {
  const idx = localLotteries.value.findIndex(l => l.id === lotteryToRemove.id);
  if (idx !== -1) {
    emit('removeLottery', lotteryToRemove);
    localLotteries.value.splice(idx, 1);
  }
};

const gridRef = ref<HTMLElement | null>(null)
</script>

<style scoped>
.lottery-section {
  margin-bottom: 2rem;
}

.section-title.medium {
  color: var(--colour-icons);
  font-family: 'Ubuntu', sans-serif;
  font-size: 20px;
  font-weight: 500;
  text-align: left;
  line-height: 1;
  margin: 0;
  display: flex;
  align-items: center;
}

.lottery-container {
  position: relative;
  width: 100%;
  padding: 0;
  margin: 16px 0 0 0;
}

.lottery-grid {
  width: 100%;
  box-sizing: border-box;
}

/* Horizontal Grid: Vertical stack of compact cards (Grid layout) */
.lottery-grid.horizontal {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--horizontal-lottery-grid-gap, 12px);
  justify-content: flex-start;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  align-items: start;
}

/* Vertical Layout: Horizontal scrollable row of large cards */
.lottery-grid.vertical {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  gap: var(--vertical-lottery-grid-gap, 12px);
  padding-bottom: var(--vertical-lottery-grid-padding-bottom, 12px);
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  /* Contain only horizontal overscroll; never block vertical page scroll */
  overscroll-behavior-x: contain;
}

.lottery-grid.vertical::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

.lottery-grid.vertical > div {
  flex: 0 0 var(--vertical-lottery-card-width, 210.78px);
  max-width: var(--vertical-lottery-card-width, 210.78px);
}

/* Keep card width fixed on large desktops — avoid stretched slots with empty gaps */
@media (min-width: 1680px) {
  .lottery-grid.vertical {
    justify-content: flex-start;
    gap: var(--vertical-lottery-grid-gap-desktop, var(--vertical-lottery-grid-gap, 8px));
  }

  .lottery-grid.vertical > div {
    flex: 0 0 var(--vertical-lottery-card-width, 210.78px);
    max-width: var(--vertical-lottery-card-width, 210.78px);
  }
}

/* Desktop/Tablet optimization - ensure 2 columns stay from 769px and above */
@media (min-width: 769px) {
  .lottery-grid.horizontal {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--horizontal-lottery-grid-gap, 12px);
  }
}

/* Tablet/iPad breakpoint - stay in 2 columns until mobile (768px) */
@media (max-width: 768px) {
  .lottery-section {
    margin-bottom: 1.25rem;
  }

  .section-header {
    justify-content: space-between;
  }

  .lottery-container {
    margin: 12px 0;
  }

  .lottery-grid.horizontal {
    grid-template-columns: 1fr;
    gap: var(--horizontal-lottery-grid-mobile-gap, var(--horizontal-lottery-grid-gap, 12px));
  }

  .lottery-grid.vertical {
    gap: var(--vertical-lottery-grid-mobile-gap, var(--vertical-lottery-grid-gap, 8px));
  }

  .lottery-grid.vertical > div {
    flex: 0 0 var(--vertical-lottery-card-mobile-width, 160px);
  }

  .lottery-container {
    margin: 0;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Swiper Navigation Styles */
.lottery-container.has-swiper {
  position: relative;
}

.swiper-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 28px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--swiper-nav-color, white);
  background: var(--swiper-nav-bg, color-mix(in srgb, var(--colour-orange-button) 85%, transparent));
  border: none;
  transition: opacity 0.25s ease, background 0.2s ease;
  opacity: 0;
  pointer-events: none;
  box-shadow: 0 4px 12px var(--shadow-color);
}

.swiper-nav.is-visible {
  opacity: 1;
  pointer-events: auto;
}

.lottery-container.has-swiper:hover .swiper-nav.is-visible {
  opacity: 1;
}

.swiper-nav:hover {
  background: var(--swiper-nav-hover-bg, var(--colour-orange-button));
}

.swiper-nav-prev {
  left: -6px;
  border-top-right-radius: 25px;
  border-bottom-right-radius: 25px;
}

.swiper-nav-next {
  right: -6px;
  border-top-left-radius: 25px;
  border-bottom-left-radius: 25px;
}

@media (max-width: 768px) {
  .swiper-nav.is-visible {
    opacity: 1;
  }
  
  .swiper-nav {
    width: 24px;
    height: 46px;
    background: var(--swiper-nav-bg, color-mix(in srgb, var(--colour-orange-button) 92%, transparent));
    box-shadow: 0 2px 8px var(--shadow-color);
  }
  
  .swiper-nav-prev {
    left: -2px;
  }

  .swiper-nav-next {
    right: -2px;
  }

  .swiper-nav:hover {
    background: var(--swiper-nav-hover-bg, var(--colour-orange-button));
  }
}
</style>
